import { useQuery } from '@tanstack/react-query';
import { Mail, Download, FileText } from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatDate } from '@/lib/utils';

interface Lead {
  id: string;
  email: string;
  name: string | null;
  company: string | null;
  resource: string;
  source: string | null;
  ipAddress: string | null;
  consented: boolean;
  createdAt: string;
}

export function PlatformLeadsPage() {
  const list = useQuery({
    queryKey: ['platform', 'leads'],
    queryFn: async () => (await api.get<{ items: Lead[]; total: number; summary: Array<{ resource: string; count: number }> }>('/api/platform-admin/leads')).data,
  });

  async function downloadCsv() {
    const res = await api.get('/api/platform-admin/leads/export', { responseType: 'blob' });
    const blob = new Blob([res.data as Blob], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `praxara-marketing-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-display text-2xl">Marketing leads</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Freemium PDF downloads + signup conversions. Captured by the marketing /resources page,
            the contact form, and the Compliance Pack signup flow.
          </p>
        </div>
        <Button onClick={downloadCsv}>
          <Download className="mr-1.5 h-4 w-4" /> Export CSV
        </Button>
      </div>

      {list.data && (
        <div className="grid gap-3 md:grid-cols-4">
          <SummaryTile label="Total leads" value={list.data.total} icon={Mail} />
          {list.data.summary.map((s) => (
            <SummaryTile key={s.resource} label={s.resource} value={s.count} icon={FileText} />
          ))}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent leads</CardTitle>
          <CardDescription>Most recent {list.data?.items.length ?? 0} of {list.data?.total ?? 0}.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Resource</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Consented</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.data?.items.map((l) => (
                <TableRow key={l.id}>
                  <TableCell className="text-xs text-muted-foreground">{formatDate(l.createdAt)}</TableCell>
                  <TableCell className="font-mono text-xs">{l.email}</TableCell>
                  <TableCell className="text-sm">{l.name ?? '—'}</TableCell>
                  <TableCell className="text-sm">{l.company ?? '—'}</TableCell>
                  <TableCell><Badge variant="secondary">{l.resource}</Badge></TableCell>
                  <TableCell className="text-xs text-muted-foreground">{l.source ?? '—'}</TableCell>
                  <TableCell>{l.consented ? <Badge variant="success">yes</Badge> : <Badge variant="warning">no</Badge>}</TableCell>
                </TableRow>
              ))}
              {list.data?.items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-sm text-muted-foreground">
                    No leads captured yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function SummaryTile({ label, value, icon: Icon }: { label: string; value: number; icon: React.ComponentType<{ className?: string }> }) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="mt-2 font-display text-2xl font-semibold">{value.toLocaleString()}</div>
    </div>
  );
}
